/**
 * Android Material Design Theme System
 * Applied only when running in Capacitor (Android app)
 */

export const androidMaterialColors = {
  // Material Design 3 Color Tokens
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb', 
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3', // Primary
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },
  surface: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  }
};

export const androidElevations = {
  0: 'shadow-none',
  1: 'shadow-sm', // 1dp
  2: 'shadow-md', // 2dp  
  3: 'shadow-lg', // 3dp
  4: 'shadow-xl', // 4dp
  6: 'shadow-2xl', // 6dp
};

export const androidMotion = {
  // Material Motion easing curves
  standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
  
  // Duration tokens
  duration: {
    short1: '50ms',
    short2: '100ms', 
    short3: '150ms',
    short4: '200ms',
    medium1: '250ms',
    medium2: '300ms',
    medium3: '350ms',
    medium4: '400ms',
    long1: '450ms',
    long2: '500ms',
    long3: '550ms',
    long4: '600ms',
  }
};

export const androidTypography = {
  // Material Design 3 Typography Scale
  displayLarge: 'text-6xl font-normal tracking-tight',
  displayMedium: 'text-5xl font-normal tracking-tight',
  displaySmall: 'text-4xl font-normal tracking-tight',
  
  headlineLarge: 'text-3xl font-normal tracking-tight',
  headlineMedium: 'text-2xl font-normal tracking-tight', 
  headlineSmall: 'text-xl font-normal tracking-tight',
  
  titleLarge: 'text-lg font-medium tracking-tight',
  titleMedium: 'text-base font-medium tracking-wide',
  titleSmall: 'text-sm font-medium tracking-wide',
  
  bodyLarge: 'text-base font-normal tracking-wide',
  bodyMedium: 'text-sm font-normal tracking-wide',
  bodySmall: 'text-xs font-normal tracking-wide',
  
  labelLarge: 'text-sm font-medium tracking-wide',
  labelMedium: 'text-xs font-medium tracking-wide',
  labelSmall: 'text-xs font-medium tracking-wide',
};

/**
 * Detects if running in Android Capacitor app
 */
export const isAndroidApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return !!(
    (window as any).Capacitor && 
    (window.navigator.userAgent.includes('Android') || 
     window.navigator.userAgent.includes('Mobile'))
  );
};

/**
 * Applies Android-specific classes conditionally
 */
export const androidClass = (androidClasses: string, webClasses: string = ''): string => {
  return isAndroidApp() ? androidClasses : webClasses;
};

/**
 * Material Design component variants for Android
 */
export const androidComponents = {
  card: {
    elevated: `${androidClass('shadow-md', 'shadow-sm')} bg-white rounded-lg border border-gray-100`,
    filled: `${androidClass('bg-gray-50', 'bg-gray-50')} rounded-lg border-0`,
    outlined: 'bg-white rounded-lg border border-gray-200',
  },
  
  fab: {
    primary: `${androidClass('shadow-lg active:shadow-xl', 'shadow-md')} bg-primary text-primary-foreground rounded-full transition-shadow duration-200`,
    secondary: `${androidClass('shadow-lg active:shadow-xl', 'shadow-md')} bg-secondary text-secondary-foreground rounded-full transition-shadow duration-200`,
  },
  
  appBar: {
    primary: `${androidClass('shadow-md', 'shadow-sm')} bg-primary text-primary-foreground`,
    surface: `${androidClass('shadow-md', 'shadow-sm')} bg-white text-gray-900 border-b border-gray-200`,
  }
};

export default {
  colors: androidMaterialColors,
  elevations: androidElevations,
  motion: androidMotion,
  typography: androidTypography,
  components: androidComponents,
  isAndroidApp,
  androidClass,
};